/**
 * Canary CLI — `triage`
 *
 * Read-only access to AI triage data for a test suite execution.
 *   canary triage get <executionId> [--cluster <id>] [--json]
 *   canary triage failures <executionId> [--cluster <id>] [--new-only] [--json]
 *   canary triage wait <executionId> [--timeout 600] [--json]
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

type Severity = 'critical' | 'high' | 'medium' | 'low';

type TriageFailure = {
  workflowRunId: string;
  workflowName: string | null;
  errorMessage: string | null;
  isNew: boolean;
  rationale: string | null;
  testRunId: string | null;
};

type TriageCluster = {
  id: string;
  rank: number;
  title: string;
  category: string;
  severity: Severity;
  failureCount: number;
  newFailureCount: number;
  summaryMd: string | null;
  suggestedActionMd: string | null;
  representativeFailureId: string | null;
  failures: TriageFailure[];
};

type TriageRun = {
  id: string;
  status:
    | 'pending'
    | 'waiting'
    | 'running'
    | 'completed'
    | 'failed'
    | 'canceled'
    | 'skipped'
    | 'timeout';
  summaryMd: string | null;
  failureCount: number;
  newFailureCount: number;
  diagnosticsPending: number;
  diagnosticsTotal: number;
  diagnosticsTimedOut: boolean;
  mostPressingClusterId: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  clusters: TriageCluster[];
};

type TriageResponse = { ok: boolean; error?: string; triage?: TriageRun | null };

const TERMINAL: TriageRun['status'][] = [
  'completed',
  'failed',
  'canceled',
  'skipped',
  'timeout',
];

function printHelp(): void {
  console.log(`canary triage — AI clustering of test suite failures (beta)

Usage:
  canary triage get <executionId|latest> [--cluster <clusterId>] [--json]
  canary triage failures <executionId|latest> [--cluster <clusterId>] [--new-only] [--json]
  canary triage wait <executionId|latest> [--timeout <seconds>] [--json]

executionId is the test-suite id from \`canary test-suite list\`.
Use \`latest\` to auto-resolve the most recent test suite.

Options:
  --json              Emit raw JSON instead of formatted output
  --cluster <id>      Restrict output to one cluster
  --new-only          (failures) Show only newly-failing tests
  --timeout <secs>    (wait) Max seconds to wait for terminal status (default 600)

Examples:
  canary triage wait latest                       Block until triage finishes on the most recent suite
  canary triage get latest                        Show full clustered detail
  canary triage get <id> --cluster <clusterId>    Drill into one cluster
  canary triage failures <id> --new-only          List only newly-failing tests
`);
}

type TestExecutionListItem = { id: string };
type TestExecutionListResponse = {
  ok: boolean;
  error?: string;
  data?: TestExecutionListItem[];
};

async function resolveLatestExecutionId(
  apiUrl: string,
  token: string
): Promise<string | null> {
  const result = await apiRequest<TestExecutionListResponse>(
    apiUrl,
    token,
    'GET',
    '/workflows/test-executions?page=1&pageSize=1&sortBy=createdAt&sortOrder=desc'
  );
  if (!result.ok) return null;
  return result.data?.[0]?.id ?? null;
}

async function resolveExecutionId(
  rawId: string,
  apiUrl: string,
  token: string
): Promise<string> {
  if (rawId !== 'latest') return rawId;
  const id = await resolveLatestExecutionId(apiUrl, token);
  if (!id) {
    console.error('Error: no recent test-suite executions found to resolve `latest`.');
    process.exit(1);
  }
  console.log(`Resolved latest test-suite execution: ${id}\n`);
  return id;
}

/**
 * Probe whether the org has the triage.beta feature flag enabled by hitting
 * the by-execution endpoint. Returns true when the API responds 200 (with or
 * without a triage row), false on TRIAGE_NOT_ENABLED or any other error.
 *
 * Used by `canary test-suite run` / `get` to gate the triage cross-link hint.
 */
export async function isTriageEnabledForExecution(
  apiUrl: string,
  token: string,
  executionId: string
): Promise<boolean> {
  try {
    const result = await apiRequest<TriageResponse & { code?: string }>(
      apiUrl,
      token,
      'GET',
      `/triage/by-execution/${executionId}`
    );
    return result.ok === true;
  } catch {
    return false;
  }
}

async function fetchTriage(
  apiUrl: string,
  token: string,
  executionId: string
): Promise<TriageRun | null> {
  const result = await apiRequest<TriageResponse>(
    apiUrl,
    token,
    'GET',
    `/triage/by-execution/${executionId}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'unknown'}`);
    process.exit(1);
  }
  return result.triage ?? null;
}

function printCluster(c: TriageCluster, isMostPressing = false): void {
  const star = isMostPressing ? ' ★' : '';
  const flagNew = c.newFailureCount > 0 ? ` (${c.newFailureCount} NEW)` : '';
  console.log(
    `#${c.rank}${star}  ${c.title}\ncategory=${c.category} severity=${c.severity} failures=${c.failureCount}${flagNew}`
  );
  if (c.summaryMd) console.log('\n' + c.summaryMd);
  if (c.suggestedActionMd) console.log('\nSuggested next step:\n' + c.suggestedActionMd);
  if (c.failures.length > 0) {
    console.log('\nFailures:');
    for (const f of c.failures) {
      const tag = f.isNew ? ' [NEW]' : '';
      console.log(`  - ${f.workflowName ?? f.workflowRunId.slice(0, 8)}${tag}`);
      if (f.errorMessage) {
        const trimmed = f.errorMessage.split('\n')[0]?.slice(0, 200);
        if (trimmed) console.log(`      ${trimmed}`);
      }
      if (f.rationale) console.log(`      why: ${f.rationale}`);
    }
  }
  console.log(`\n  cluster id: ${c.id}`);
}

function printTriageSummary(t: TriageRun, executionId: string): void {
  console.log(`triage ${t.id} — status=${t.status}`);
  console.log(
    `failures=${t.failureCount} (new=${t.newFailureCount}) clusters=${t.clusters.length}` +
      (t.diagnosticsTimedOut ? ' [partial diagnostics]' : '')
  );
  if (t.summaryMd) console.log('\n' + t.summaryMd);
  if (t.errorMessage) console.log(`error: ${t.errorMessage}`);
  if (t.clusters.length === 0) return;

  const sorted = [...t.clusters].sort((a, b) => a.rank - b.rank);
  for (const c of sorted) {
    console.log('\n' + '─'.repeat(60));
    printCluster(c, t.mostPressingClusterId === c.id);
  }

  console.log('\n' + '─'.repeat(60));
  console.log('Next:');
  console.log(
    `  canary triage get ${executionId} --cluster <clusterId>     drill into a single cluster`
  );
  console.log(
    `  canary triage failures ${executionId} [--new-only]          flat failure list`
  );
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const rawId = argv[0];
  if (!rawId || rawId.startsWith('--')) {
    console.error('Error: missing <executionId>. Use `latest` to auto-resolve the most recent suite.');
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');
  const clusterId = getArgValue(argv, '--cluster');
  const executionId = await resolveExecutionId(rawId, apiUrl, token);
  const triage = await fetchTriage(apiUrl, token, executionId);

  if (json) {
    if (clusterId && triage) {
      const cluster = triage.clusters.find((c) => c.id === clusterId) ?? null;
      console.log(JSON.stringify(cluster, null, 2));
    } else {
      console.log(JSON.stringify(triage, null, 2));
    }
    return;
  }

  if (!triage) {
    console.log('No triage run for this execution yet.');
    return;
  }
  if (clusterId) {
    const cluster = triage.clusters.find((c) => c.id === clusterId);
    if (!cluster) {
      console.error(`Error: cluster ${clusterId} not found in triage ${triage.id}`);
      process.exit(1);
    }
    printCluster(cluster, triage.mostPressingClusterId === cluster.id);
    return;
  }
  printTriageSummary(triage, executionId);
}

async function handleFailures(argv: string[], apiUrl: string, token: string): Promise<void> {
  const rawId = argv[0];
  if (!rawId || rawId.startsWith('--')) {
    console.error('Error: missing <executionId>. Use `latest` to auto-resolve the most recent suite.');
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');
  const newOnly = hasFlag(argv, '--new-only');
  const clusterId = getArgValue(argv, '--cluster');
  const executionId = await resolveExecutionId(rawId, apiUrl, token);
  const triage = await fetchTriage(apiUrl, token, executionId);

  if (!triage) {
    if (json) console.log('[]');
    else console.log('No triage run for this execution yet.');
    return;
  }

  const allFailures: Array<TriageFailure & { clusterId: string; clusterTitle: string }> = [];
  for (const c of triage.clusters) {
    if (clusterId && c.id !== clusterId) continue;
    for (const f of c.failures) {
      if (newOnly && !f.isNew) continue;
      allFailures.push({ ...f, clusterId: c.id, clusterTitle: c.title });
    }
  }

  if (json) {
    console.log(JSON.stringify(allFailures, null, 2));
    return;
  }
  if (allFailures.length === 0) {
    console.log('No failures matched.');
    return;
  }
  for (const f of allFailures) {
    const tag = f.isNew ? ' [NEW]' : '';
    console.log(
      `  ${f.workflowName ?? f.workflowRunId.slice(0, 8)}${tag}  → "${f.clusterTitle}"`
    );
    if (f.errorMessage) {
      const trimmed = f.errorMessage.split('\n')[0]?.slice(0, 200);
      if (trimmed) console.log(`      ${trimmed}`);
    }
  }
}

async function handleWait(argv: string[], apiUrl: string, token: string): Promise<void> {
  const rawId = argv[0];
  if (!rawId || rawId.startsWith('--')) {
    console.error('Error: missing <executionId>. Use `latest` to auto-resolve the most recent suite.');
    process.exit(1);
  }
  const json = hasFlag(argv, '--json');
  const timeoutSec = Number.parseInt(getArgValue(argv, '--timeout') ?? '600', 10);
  const executionId = await resolveExecutionId(rawId, apiUrl, token);
  const deadline = Date.now() + timeoutSec * 1000;

  let last: TriageRun | null = null;
  while (Date.now() < deadline) {
    last = await fetchTriage(apiUrl, token, executionId);
    if (last && TERMINAL.includes(last.status)) {
      if (json) console.log(JSON.stringify(last, null, 2));
      else printTriageSummary(last, executionId);
      if (last.status === 'failed') process.exit(2);
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 5000));
  }

  if (json) {
    console.log(JSON.stringify({ timedOut: true, last }, null, 2));
  } else {
    console.error(`Timed out after ${timeoutSec}s`);
    if (last) printTriageSummary(last, executionId);
  }
  process.exit(1);
}

export async function runTriage(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'failures':
      await handleFailures(rest, apiUrl, token);
      break;
    case 'wait':
      await handleWait(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printHelp();
      process.exit(1);
  }
}
